// Entry point for esbuild; add front-end behaviour here as needed.
import * as ActiveStorage from '@rails/activestorage'
import 'trix'
import '@rails/actiontext'

ActiveStorage.start()

document.documentElement.classList.add('dark')
document.documentElement.setAttribute('data-theme', 'dark')

const initMobileNav = () => {
  const toggle = document.querySelector('[data-mobile-menu-toggle]')
  const menu = document.querySelector('[data-mobile-menu]')
  if (!toggle || !menu) return
  const mq = window.matchMedia('(max-width: 767px)')
  const syncVisibility = () => {
    if (mq.matches) {
      toggle.hidden = false
    } else {
      toggle.hidden = true
      toggle.setAttribute('aria-expanded', 'false')
      menu.hidden = true
      menu.classList.add('hidden')
    }
  }
  const onToggle = () => {
    if (!mq.matches) return
    const isOpen = toggle.getAttribute('aria-expanded') === 'true'
    toggle.setAttribute('aria-expanded', (!isOpen).toString())
    menu.hidden = isOpen
    menu.classList.toggle('hidden', isOpen)
  }
  syncVisibility()
  mq.addEventListener('change', syncVisibility)
  toggle.addEventListener('click', onToggle)
}

const REPLY_THEME_KEY = 'edudata.replyTheme'

const applyReplyTheme = (root, theme) => {
  const normalizedTheme = theme === 'light' ? 'light' : 'dark'
  root.dataset.replyTheme = normalizedTheme

  root.querySelectorAll('[data-reply-theme-toggle]').forEach((toggle) => {
    toggle.setAttribute('aria-pressed', (normalizedTheme === 'light').toString())
    const label = normalizedTheme === 'light' ? 'Switch to dark reply theme' : 'Switch to light reply theme'
    toggle.setAttribute('aria-label', label)
    toggle.setAttribute('title', label)
  })

  root.querySelectorAll('[data-reply-theme-icon-dark]').forEach((icon) => {
    icon.classList.toggle('hidden', normalizedTheme === 'dark')
  })

  root.querySelectorAll('[data-reply-theme-icon-light]').forEach((icon) => {
    icon.classList.toggle('hidden', normalizedTheme !== 'dark')
  })
}

const initReplyThemeToggles = () => {
  const roots = document.querySelectorAll('[data-reply-theme-root]')
  if (!roots.length) return

  const storedTheme = localStorage.getItem(REPLY_THEME_KEY)
  const initialTheme = storedTheme === 'light' ? 'light' : 'dark'

  roots.forEach((root) => {
    applyReplyTheme(root, initialTheme)

    root.querySelectorAll('[data-reply-theme-toggle]').forEach((toggle) => {
      if (toggle.dataset.replyThemeBound === 'true') return
      toggle.dataset.replyThemeBound = 'true'

      toggle.addEventListener('click', () => {
        const nextTheme = root.dataset.replyTheme === 'light' ? 'dark' : 'light'
        applyReplyTheme(root, nextTheme)
        localStorage.setItem(REPLY_THEME_KEY, nextTheme)
      })
    })
  })
}

const initForumLikeToggles = () => {
  const forms = document.querySelectorAll('form[data-like-toggle-form]')
  if (!forms.length) return

  forms.forEach((form) => {
    if (form.dataset.likeToggleBound === 'true') return
    form.dataset.likeToggleBound = 'true'

    form.addEventListener('submit', async (event) => {
      event.preventDefault()

      const methodOverride = form.querySelector('input[name="_method"]')?.value
      const method = (methodOverride || form.method || 'post').toUpperCase()
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

      try {
        const response = await fetch(form.action, {
          method,
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': csrfToken || ''
          },
          credentials: 'same-origin'
        })

        if (!response.ok) return
        const payload = await response.json()
        if (!payload?.html) return

        const card = form.closest('[data-reply-card]')
        if (!card) return

        card.outerHTML = payload.html
        initForumLikeToggles()
      } catch (_error) {
        // If fetch fails, leave current UI state as-is.
      }
    })
  })
}

const initReplyAttachmentUploadGuard = () => {
  if (document.body.dataset.replyAttachmentGuardBound === 'true') return
  document.body.dataset.replyAttachmentGuardBound = 'true'

  const isTrixEditor = (editor) => {
    if (!(editor instanceof HTMLElement)) return false
    return editor.tagName.toLowerCase() === 'trix-editor'
  }

  const isReplyEditor = (editor) => {
    if (!(editor instanceof HTMLElement)) return false
    if (editor.closest('[data-reply-compose]')) return true

    const inputId = editor.getAttribute('input')
    if (!inputId) return false
    const input = document.getElementById(inputId)
    if (!(input instanceof HTMLInputElement)) return false

    return input.name === 'forum_post[body]'
  }

  const normalizeUrl = (value) => {
    const trimmed = value.trim()
    if (!trimmed) return ''
    if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed
    return `https://${trimmed}`
  }

  const uploadReplyEmbeddedImage = async (file) => {
    const formData = new FormData()
    formData.append('file', file)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    const response = await fetch('/forum/media', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken
      },
      credentials: 'same-origin',
      body: formData
    })

    const payload = await response.json().catch(() => ({}))
    if (!response.ok || !payload?.url) {
      throw new Error(payload?.error || 'Image upload failed.')
    }

    return payload.url
  }

  document.addEventListener('trix-initialize', (event) => {
    const editor = event.target
    if (!isTrixEditor(editor)) return

    const toolbarId = editor.getAttribute('toolbar')
    if (!toolbarId) return

    const toolbar = document.getElementById(toolbarId)
    if (!(toolbar instanceof HTMLElement)) return
    if (toolbar.dataset.customLinkBound === 'true') return

    const trixEditor = editor.editor
    if (!trixEditor) return

    const attachButton = toolbar?.querySelector('.trix-button--icon-attach')
    if (isReplyEditor(editor) && attachButton instanceof HTMLElement) {
      attachButton.setAttribute('title', 'Embed image')
      attachButton.setAttribute('aria-label', 'Embed image')
    }

    const linkButton = toolbar?.querySelector('.trix-button--icon-link')
    if (linkButton instanceof HTMLElement) {
      linkButton.removeAttribute('data-trix-action')
      linkButton.removeAttribute('data-trix-attribute')
      linkButton.setAttribute('type', 'button')
      linkButton.classList.remove('trix-button--icon')
      linkButton.classList.add('trix-button--icon-link-custom')
      linkButton.textContent = 'Link'
      linkButton.setAttribute('title', 'Add link with display text')
      linkButton.setAttribute('aria-label', 'Add link with display text')
    }

    const nativeLinkDialog = toolbar?.querySelector('.trix-dialog--link')
    if (nativeLinkDialog instanceof HTMLElement) {
      nativeLinkDialog.style.display = 'none'
      nativeLinkDialog.setAttribute('aria-hidden', 'true')
    }

    const dialogs = toolbar.querySelector('.trix-dialogs')
    if (!(dialogs instanceof HTMLElement)) return

    const customLinkDialog = document.createElement('div')
    customLinkDialog.className = 'trix-dialog trix-dialog--link-text-custom'
    customLinkDialog.hidden = true
    customLinkDialog.innerHTML = `
      <form class="trix-dialog__link-text-fields" data-custom-link-form>
        <label>
          <span>Link URL</span>
          <input class="trix-input trix-input--dialog" type="url" name="href" placeholder="https://example.com" required>
        </label>
        <label>
          <span>Text to show</span>
          <input class="trix-input trix-input--dialog" type="text" name="display_text" placeholder="Watch the webinar">
        </label>
        <span class="trix-button-group">
          <button class="trix-button trix-button--dialog" type="submit" data-custom-link-submit>Add link</button>
          <button class="trix-button trix-button--dialog" type="button" data-custom-link-cancel>Cancel</button>
        </span>
      </form>
    `
    dialogs.appendChild(customLinkDialog)

    const customLinkForm = customLinkDialog.querySelector('[data-custom-link-form]')
    const customLinkCancel = customLinkDialog.querySelector('[data-custom-link-cancel]')
    const customLinkSubmit = customLinkDialog.querySelector('[data-custom-link-submit]')
    const customLinkUrl = customLinkDialog.querySelector('input[name="href"]')
    const customLinkText = customLinkDialog.querySelector('input[name="display_text"]')
    let linkSelection = [0, 0]
    let selectedLinkText = ''

    const closeCustomLinkDialog = () => {
      customLinkDialog.hidden = true
      linkButton?.classList.remove('trix-active')
    }

    const linkContextAtSelection = (selection) => {
      const documentModel = trixEditor.getDocument()
      const [start, end] = selection
      const positions = [start, Math.max(start, end - 1), Math.max(0, start - 1)]

      for (const position of [...new Set(positions)]) {
        const attributes = documentModel.getCommonAttributesAtPosition(position)
        if (!attributes?.href) continue

        const range = documentModel.getRangeOfCommonAttributeAtPosition('href', position)
        const text = documentModel.toString().slice(range[0], range[1])
        return { href: attributes.href, range, text }
      }

      return null
    }

    const linkContextForElement = (anchor) => {
      const domRange = document.createRange()
      domRange.selectNodeContents(anchor)

      const locationRange = trixEditor.selectionManager.createLocationRangeFromDOMRange(domRange, { strict: false })
      if (!locationRange) return null

      const documentModel = trixEditor.getDocument()
      const range = documentModel.rangeFromLocationRange(locationRange)
      const attributes = documentModel.getCommonAttributesAtRange(range)

      return {
        href: attributes.href || anchor.getAttribute('href'),
        range,
        text: documentModel.toString().slice(range[0], range[1])
      }
    }

    const openCustomLinkDialog = ({ selection, href = '', text = '', editing = false }) => {
      linkSelection = selection
      selectedLinkText = text
      customLinkUrl.value = href
      customLinkText.value = text
      customLinkSubmit.textContent = editing ? 'Update link' : 'Add link'
      customLinkDialog.hidden = false
      linkButton?.classList.add('trix-active')
      customLinkUrl.focus()
      customLinkUrl.select()
    }

    if (customLinkCancel instanceof HTMLButtonElement) {
      customLinkCancel.addEventListener('click', closeCustomLinkDialog)
    }

    if (customLinkForm instanceof HTMLFormElement && customLinkUrl instanceof HTMLInputElement && customLinkText instanceof HTMLInputElement && customLinkSubmit instanceof HTMLButtonElement) {
      customLinkForm.addEventListener('submit', (submitEvent) => {
        submitEvent.preventDefault()

        const href = normalizeUrl(customLinkUrl.value)
        if (!href) return

        const linkText = customLinkText.value.trim() || selectedLinkText || href
        const [start, end] = linkSelection
        trixEditor.setSelectedRange([start, end])

        if (start === end || linkText !== selectedLinkText) {
          trixEditor.insertString(linkText)
          const position = trixEditor.getPosition()
          trixEditor.setSelectedRange([position - linkText.length, position])
        }

        trixEditor.activateAttribute('href', href)
        closeCustomLinkDialog()
      })
    }

    toolbar.dataset.customLinkBound = 'true'

    toolbar.addEventListener(
      'click',
      (clickEvent) => {
        const target = clickEvent.target
        if (!(target instanceof Element)) return

        const clickedLinkButton = target.closest('.trix-button--icon-link, .trix-button--icon-link-custom')
        if (!(clickedLinkButton instanceof HTMLElement)) return

        clickEvent.preventDefault()
        clickEvent.stopImmediatePropagation()

        const selection = trixEditor.getSelectedRange()
        const existingLink = linkContextAtSelection(selection)

        if (existingLink) {
          trixEditor.setSelectedRange(existingLink.range)
          openCustomLinkDialog({
            selection: existingLink.range,
            href: existingLink.href,
            text: existingLink.text,
            editing: true
          })
        } else {
          const text = trixEditor.getDocument().toString().slice(selection[0], selection[1])
          openCustomLinkDialog({ selection, text })
        }
      },
      true
    )

    editor.addEventListener(
      'dblclick',
      (doubleClickEvent) => {
        const target = doubleClickEvent.target
        if (!(target instanceof Element)) return

        const anchor = target.closest('a[href]')
        if (!(anchor instanceof HTMLAnchorElement) || !editor.contains(anchor)) return

        doubleClickEvent.preventDefault()

        const existingLink = linkContextForElement(anchor)
        if (!existingLink) return

        trixEditor.setSelectedRange(existingLink.range)
        window.requestAnimationFrame(() => {
          openCustomLinkDialog({
            selection: existingLink.range,
            href: existingLink.href,
            text: existingLink.text,
            editing: true
          })
        })
      },
      true
    )
  })

  document.addEventListener('trix-file-accept', (event) => {
    const editor = event.target
    if (!(editor instanceof HTMLElement)) return
    if (!isReplyEditor(editor)) return

    const file = event.file
    if (file?.type?.startsWith('image/')) return

    event.preventDefault()
    window.alert('Only images can be embedded in the comment. Use the "Attachments" picker below for documents.')
  })

  document.addEventListener(
    'trix-attachment-add',
    async (event) => {
      const editor = event.target
      if (!(editor instanceof HTMLElement)) return
      if (!isReplyEditor(editor)) return

      const attachment = event.attachment
      if (!attachment?.file) return

      // Prevent ActionText direct-upload handlers for reply composer; use our S3 uploader path instead.
      event.preventDefault()
      event.stopImmediatePropagation()

      const file = attachment.file
      if (!file.type?.startsWith('image/')) {
        attachment.remove()
        window.alert('Only images can be embedded in the comment. Use the "Attachments" picker below for documents.')
        return
      }

      const localPreviewUrl = URL.createObjectURL(file)
      if (typeof attachment.setPreviewURL === 'function') {
        attachment.setPreviewURL(localPreviewUrl)
      }

      try {
        attachment.setUploadProgress(15)
        const embeddedUrl = await uploadReplyEmbeddedImage(file)
        attachment.setAttributes({
          url: embeddedUrl,
          href: embeddedUrl,
          filename: file.name,
          filesize: file.size,
          contentType: file.type,
          previewable: true
        })
        attachment.setUploadProgress(100)
      } catch (error) {
        attachment.remove()
        const message = error instanceof Error ? error.message : 'Image upload failed. Please try again.'
        window.alert(`${message} If this keeps happening live, check S3/CORS configuration.`)
      } finally {
        URL.revokeObjectURL(localPreviewUrl)
      }
    },
    true
  )

  document.addEventListener('direct-upload:error', (event) => {
    const target = event.target
    if (!(target instanceof HTMLElement)) return
    if (!isReplyEditor(target)) return

    event.preventDefault()
    const message = event.detail?.error || 'Image upload failed. Please try again.'
    window.alert(`${message} If this keeps happening live, check S3 CORS rules for ActiveStorage direct uploads.`)
  })
}

const initAttachmentDisplayNameInputs = () => {
  document.querySelectorAll('[data-attachment-label-input]').forEach((fileInput) => {
    if (!(fileInput instanceof HTMLInputElement)) return
    if (fileInput.dataset.attachmentLabelsBound === 'true') return

    const form = fileInput.closest('form')
    const labelsContainer = form?.querySelector('[data-new-attachment-labels]')
    if (!(labelsContainer instanceof HTMLElement)) return

    fileInput.dataset.attachmentLabelsBound = 'true'
    fileInput.addEventListener('change', () => {
      labelsContainer.replaceChildren()

      Array.from(fileInput.files || []).forEach((file) => {
        const wrapper = document.createElement('div')
        const label = document.createElement('label')
        const textInput = document.createElement('input')
        const defaultText = file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())

        label.className = 'block text-xs font-medium text-slate-600 dark:text-slate-300'
        label.textContent = `Text shown for ${file.name}`

        textInput.type = 'text'
        textInput.name = 'page[new_attachment_display_names][]'
        textInput.value = defaultText
        textInput.maxLength = 160
        textInput.className = 'mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100'

        label.appendChild(textInput)
        wrapper.appendChild(label)
        labelsContainer.appendChild(wrapper)
      })
    })
  })
}

const initReplyImageViewer = () => {
  if (document.body.dataset.replyImageViewerBound === 'true') return
  document.body.dataset.replyImageViewerBound = 'true'

  const overlay = document.createElement('div')
  overlay.className = 'reply-image-viewer'
  overlay.setAttribute('aria-hidden', 'true')
  overlay.innerHTML = `
    <button type="button" class="reply-image-viewer__close">Close</button>
    <img class="reply-image-viewer__img" alt="Expanded reply image" />
  `
  document.body.appendChild(overlay)

  const image = overlay.querySelector('.reply-image-viewer__img')
  const closeButton = overlay.querySelector('.reply-image-viewer__close')

  const close = () => {
    overlay.classList.remove('is-open')
    overlay.setAttribute('aria-hidden', 'true')
    if (image instanceof HTMLImageElement) image.src = ''
  }

  const open = (src) => {
    if (!(image instanceof HTMLImageElement) || !src) return
    image.src = src
    overlay.classList.add('is-open')
    overlay.setAttribute('aria-hidden', 'false')
  }

  closeButton?.addEventListener('click', close)
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) close()
  })

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close()
  })

  document.addEventListener('click', (event) => {
    const target = event.target
    if (!(target instanceof HTMLImageElement)) return
    if (!target.closest('.reply-card .trix-content')) return
    event.preventDefault()
    event.stopPropagation()
    open(target.currentSrc || target.src)
  })
}

document.addEventListener('turbo:load', initMobileNav)
document.addEventListener('DOMContentLoaded', initMobileNav)
document.addEventListener('turbo:load', initReplyThemeToggles)
document.addEventListener('DOMContentLoaded', initReplyThemeToggles)
document.addEventListener('turbo:load', initForumLikeToggles)
document.addEventListener('DOMContentLoaded', initForumLikeToggles)
document.addEventListener('turbo:load', initReplyAttachmentUploadGuard)
document.addEventListener('DOMContentLoaded', initReplyAttachmentUploadGuard)
document.addEventListener('turbo:load', initReplyImageViewer)
document.addEventListener('DOMContentLoaded', initReplyImageViewer)
document.addEventListener('turbo:load', initAttachmentDisplayNameInputs)
document.addEventListener('DOMContentLoaded', initAttachmentDisplayNameInputs)

if (document.readyState !== 'loading') {
  initMobileNav()
  initReplyThemeToggles()
  initForumLikeToggles()
  initReplyAttachmentUploadGuard()
  initReplyImageViewer()
  initAttachmentDisplayNameInputs()
}
